import {
  ClaimedTicketRequestRow,
  GetClaimedTicketRequestsParams,
} from "../interface";
import { User } from "../models/User";
import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";
import { deleteDeputyReport, getPublicReports } from "./Report";
import { addTVChannelReportResponse } from "./Response";
const lUser: User = JSON.parse(localStorage.getItem("user") as string);

type UpdateDeputyReportParams = {
  id: number;
  status: "ACKNOWLEDGED" | "DUPLICATE" | "REJECTED";
  admin_comment: string;
  admin_user_id: string;
};

type UpdateDeputyTVReportParams = {
  id: string | number;
  status: "ACKNOWLEDGED" | "DUPLICATE" | "REJECTED";
  user_id: string; // admin user id
  admin_comment: string; // required
};

const reportApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // getDeputyReports: build.query<any, any>({
    //   queryFn: async (year) => {
    //     let { data } = await supabase
    //       .from("dupty_report")
    //       .select(
    //         "*,user:users!user_Id(id,name,profile_picture,email,phone_number,last_name,deputy_control_number,sub_role),attachments:deputy_report_attachments(*),admin:users!status_updated_by(id,name,last_name)"
    //       )
    //       .gte("created_at", `${year}-01-01`)
    //       .lte("created_at", `${year}-12-31`)
    //       .order("created_at", { ascending: false });
    //     return { data };
    //   },
    //   providesTags: ["reports"],
    // }),
    getDeputyReportById: build.query<any, string>({
      queryFn: async (id) => {
        let { data, error } = await supabase
          .from("dupty_report")
          .select(
            "*,user:users!user_Id(*),attachments:deputy_report_attachments(*),admin:users!status_updated_by(id,name,last_name)",
          )
          .eq("id", id)
          .single();

        if (error) return { error };

        return { data };
      },
      providesTags: (result, error, id) => [{ type: "reports", id }],
    }),
    getDeputyReports: build.query<any, any>({
      queryFn: async ({
        startDate,
        endDate,
        violationFilter,
        statusFilter,
        typeFilter,
        start,
        end,
        sortColumn,
        sortDirection,
      }) => {
        let query = supabase
          .from("dupty_report")
          .select(
            `
        id,
        created_at,
        status,
        has_violation,
        report_type,
        location,
        description,
        additional_info,
        puv_plate_no,
        puv_route,
        user:users!user_Id!inner(
          id,
          name,
          profile_picture,
          last_name,
          sub_role,
          deputy_control_number
        )
        `,
            { count: "exact" },
          )
          .gte("created_at", `${startDate} 00:00:00`)
          .lte("created_at", `${endDate} 23:59:59`)
          .eq("user.sub_role", "DEPUTY")
          .order(sortColumn || "created_at", { ascending: sortDirection === "asc" })
          .range(start, end);

        if (statusFilter && statusFilter !== "ALL") {
          query = query.eq("status", statusFilter);
        }

        if (violationFilter === "WITH") {
          query = query.eq("has_violation", true);
        } else if (violationFilter === "NONE") {
          query = query.eq("has_violation", false);
        }

        if (typeFilter && typeFilter !== "ALL") {
          query = query.eq("report_type", typeFilter);
        }

        const { data, count, error } = await query;

        if (error) {
          console.error("OTHER VENUE REPORT ERROR:", error);
          return { error };
        }

        return {
          data: {
            list: data || [],
            count: count || 0,
          },
        };
      },
      providesTags: ["reports"],
    }),
    getDeputyReportStatistics: build.query<any, any>({
      queryFn: async ({ year, month, limit, offset }) => {
        const { data, error } = await supabase.rpc("deputy_report_statistics", {
          p_year: year,
          p_month: month, // ✅ null = whole year
          p_limit: limit,
          p_offset: offset,
        });

        if (error) throw error;
        return { data };
      },
      providesTags: ["reports"],
    }),
    getDeputyReportSummaryTwo: build.query<any, any>({
      queryFn: async (params) => {
        const { data, error } = await supabase.rpc(
          "deputy_report_summary_v2",
          params,
        );
        if (error) throw error;
        return { data };
      },
    }),
    getDeputyReportSummary: build.query<
      {
        total: number;
        movie: number;
        tv: number;
        puv: number;
        "video-pm": number;
        "non-video-pm": number;
        others: number;
      },
      { year: number; month: number }
    >({
      queryFn: async ({ year, month }) => {
        try {
          const { data, error } = await supabase.rpc(
            "deputy_report_summary_two",
            {
              p_year: year,
              p_month: month,
            },
          );
          if (error) throw error;
          return { data: data as any };
        } catch (err: any) {
          return { error: { status: err?.status ?? 500, data: err } as any };
        }
      },
      providesTags: ["reports"],
    }),
    getDeputyDashboardSummaryV3: build.query<
      {
        total: number;
        movie: number;
        tv: number;
        puv: number;
        "video-pm": number;
        "non-video-pm": number;
        others: number;
        with_violation: number;
        without_violation: number;
      },
      { year: number; month: number; violationFilter: string; endorserFilter: string }
    >({
      queryFn: async ({ year, month, violationFilter, endorserFilter }) => {
        try {
          const { data, error } = await supabase.rpc(
            "deputy_dashboard_summary_v3",
            {
              p_year: year,
              p_month: month === 0 ? null : month,
              p_violation: violationFilter,
              p_endorser: endorserFilter,
            },
          );
          if (error) throw error;
          return { data: data as any };
        } catch (err: any) {
          return { error: { status: err?.status ?? 500, data: err } as any };
        }
      },
      providesTags: ["reports"],
    }),
    getDeputyDashboardMonthlyTrend: build.query<
      { month: number; total: number }[],
      { year: number; violationFilter: string; endorserFilter: string }
    >({
      queryFn: async ({ year, violationFilter, endorserFilter }) => {
        try {
          const { data, error } = await supabase.rpc(
            "deputy_dashboard_monthly_trend",
            {
              p_year: year,
              p_violation: violationFilter,
              p_endorser: endorserFilter,
            },
          );
          if (error) throw error;
          return { data: data as any };
        } catch (err: any) {
          return { error: { status: err?.status ?? 500, data: err } as any };
        }
      },
      providesTags: ["reports"],
    }),
    getDistinctEndorsers: build.query<string[], { year: number }>({
      queryFn: async ({ year }) => {
        try {
          const { data, error } = await supabase.rpc("get_distinct_endorsers", {
            p_year: year,
          });
          if (error) throw error;
          // data is an array of objects like { deputy_endorser: string }
          const endorsers = data.map((d: any) => d.deputy_endorser).filter(Boolean);
          return { data: endorsers };
        } catch (err: any) {
          return { error: { status: err?.status ?? 500, data: err } as any };
        }
      },
    }),
    getDeputyReportAttachments: build.query<any, any>({
      queryFn: async (id) => {
        let { data } = await supabase
          .from("deputy_report_attachments")
          .select("*")
          .eq("deputy_report_id", id);
        return { data };
      },
      providesTags: ["reports"],
    }),
    getPublicReports: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("report")
          .select(
            "*,user:users!users_Id(id,name,profile_picture,email,phone_number,last_name),attachments:report_attachments(*),admin:users!status_updated_by(id,profile_picture,name,last_name)",
          )
          .in("status", ["PENDING", "ACKNOWLEDGED", "REJECTED", "DUPLICATE"])
          .eq("user.role", "USER")
          .order("created_at", { ascending: false });
        return { data };
      },
      providesTags: ["reports"],
    }),
    getDateFilteredPublicReports: build.query<any, any>({
      queryFn: async ({ startDate, endDate }) => {
        let { data } = await supabase
          .from("report")
          .select(
            "*,user:users!users_Id(id,name,profile_picture,email,phone_number,last_name),attachments:report_attachments(*),admin:users!status_updated_by(id,profile_picture,name,last_name)",
          )
          .in("status", ["ACKNOWLEDGED"])
          .gte("created_at", startDate) // Filter for dates greater than or equal to startDate
          .lte("created_at", endDate)
          .order("created_at", { ascending: false });
        return { data };
      },
      providesTags: ["reports"],
    }),
    getPublicReportById: build.query<any, string | number>({
      queryFn: async (id) => {
        try {
          const { data, error } = await supabase
            .from("report")
            .select(
              "*,user:users!users_Id(id,name,profile_picture,email,phone_number,last_name),attachments:report_attachments(*),admin:users!status_updated_by(id,profile_picture,name,last_name)",
            )
            .eq("id", id)
            .single();

          if (error) {
            console.error("Error fetching public report by id:", error);
            return { error };
          }

          // Fetch public responses separately (table has columns: id, created_at, response, report_id)
          const { data: responsesData, error: respError } = await supabase
            .from("public_responses")
            .select("*")
            .eq("report_id", id)
            .order("created_at", { ascending: false });

          if (respError) {
            console.warn("Could not fetch public responses:", respError);
          }

          const reportWithResponses = {
            ...data,
            responses: responsesData || [],
          };

          return { data: reportWithResponses };
        } catch (err: any) {
          console.error("Unexpected error in getPublicReportById:", err);
          return { error: { message: err?.message || "Failed to load public report" } };
        }
      },
      providesTags: (result, error, id) => [{ type: "reports", id }],
    }),
    getDeputyReportId: build.query<any, any>({
      queryFn: async (id: string) => {
        let { data } = await supabase
          .from("dupty_report")
          .delete()
          .eq("id", id);
        return { data };
      },
      providesTags: ["reports"],
    }),
    deletePublicReport: build.mutation<any, any>({
      queryFn: async (id: string) => {
        let { data } = await supabase.from("report").delete().eq("id", id);
        return { data };
      },
      invalidatesTags: ["reports"],
    }),
    deleteDeputyReport: build.mutation<any, any>({
      queryFn: async (id: string) => {
        let { data } = await supabase
          .from("dupty_report")
          .delete()
          .eq("id", id);
        return { data };
      },
      invalidatesTags: ["reports"],
    }),
    addPublicResponse: build.mutation<any, any>({
      queryFn: async (response) => {
        let { data } = await supabase
          .from("public_responses")
          .insert(response)
          .single();
        return { data };
      },
      invalidatesTags: ["reports"],
    }),
    addDeputyResponse: build.mutation<any, any>({
      queryFn: async (response) => {
        let { data } = await supabase
          .from("deputy_responses")
          .insert(response)
          .single();
        return { data };
      },
      invalidatesTags: ["reports"],
    }),
    addTVChannelReportResponse: build.mutation<any, any>({
      queryFn: async (response) => {
        let { data } = await supabase
          .from("tv_channel_responses")
          .insert(response)
          .single();
        return { data };
      },
      invalidatesTags: ["reports"],
    }),

    getTVChannelReports: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase.from("tv_program_report").select("*");
        return { data };
      },
      providesTags: ["reports"],
    }),

    deleteTVChannelReport: build.mutation<any, any>({
      queryFn: async (id) => {
        let { data } = await supabase
          .from("tv_program_report")
          .delete()
          .eq("id", id);
        return { data };
      },
      invalidatesTags: ["reports"],
    }),

    //TEST
    getTVProgramReports: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("tv_program_reports")
          .select(
            "*,tv_program:tv_programs(id,title,image),user:users(id,name,profile_picture)",
          );
        return { data };
      },
      providesTags: ["reports"],
    }),

    // getDeputyTVReports: build.query<any, any>({
    //   queryFn: async (year) => {
    //     let { data } = await supabase
    //       .from("deputy_tv_reports")
    //       .select(
    //         "*,user:users!deputy_user_id(id,name,profile_picture,email,phone_number,last_name,deputy_control_number,sub_role),attachments:deputy_tv_report_attachments(*),admin:users!status_updated_by(id,name,last_name)"
    //       )
    //       .gte("created_at", `${year}-01-01`)
    //       .lte("created_at", `${year}-12-31`)
    //       .in("report_status", ["PENDING", "ACKNOWLEDGED", "REJECTED", "DUPLICATE"])
    //       .order("created_at", { ascending: false });
    //     return { data };
    //   },
    //   providesTags: ["reports"],
    // }),

    getDeputyTVReportById: build.query<any, string>({
      queryFn: async (id) => {
        let { data, error } = await supabase
          .from("deputy_tv_reports")
          .select(
            "*,user:users!deputy_user_id(*),attachments:deputy_tv_report_attachments(*),admin:users!status_updated_by(id,name,last_name)",
          )
          .eq("id", id)
          .single();

        if (error) return { error };

        return { data };
      },
      providesTags: (result, error, id) => [{ type: "reports", id }],
    }),

    getDeputyTVReports: build.query<any, any>({
      queryFn: async ({
        startDate,
        endDate,
        statusFilter,
        violationFilter,
        tvNetworkSearch,
        start,
        end,
        sortColumn,
        sortDirection,
      }) => {
        let query = supabase
          .from("deputy_tv_reports")
          .select(
            `
        id,
        created_at,
        report_status,
        has_violation,
        tv_network,
        tv_program,
        rating,
        user:users!deputy_user_id!inner(
          id,
          name,
          profile_picture,
          last_name,
          sub_role,
          deputy_control_number
        )
        `,
            { count: "exact" },
          )
          .gte("created_at", `${startDate} 00:00:00`)
          .lte("created_at", `${endDate} 23:59:59`)
          .eq("user.sub_role", "DEPUTY")
          .order(sortColumn || "created_at", { ascending: sortDirection === "asc" })
          .range(start, end);

        if (statusFilter && statusFilter !== "ALL") {
          query = query.eq("report_status", statusFilter);
        }

        if (violationFilter === "WITH") {
          query = query.eq("has_violation", true);
        } else if (violationFilter === "NONE") {
          query = query.eq("has_violation", false);
        }

        if (tvNetworkSearch && tvNetworkSearch.trim() !== "") {
          query = query.ilike("tv_network", `%${tvNetworkSearch.trim()}%`);
        }

        const { data, count, error } = await query;

        if (error) {
          console.error("TV REPORT ERROR:", error);
          return { error };
        }

        return {
          data: {
            list: data || [],
            count: count || 0,
          },
        };
      },
      providesTags: ["reports"],
    }),

    updateDeputyTVReportStatus: build.mutation<any, UpdateDeputyTVReportParams>(
      {
        queryFn: async ({ id, status, user_id, admin_comment }) => {
          const { error } = await supabase
            .from("deputy_tv_reports")
            .update({
              report_status: status,
              status_updated_by: user_id,
              admin_comment, // <-- you said you added this colum
            })
            .eq("id", id);

          if (error) return { error };
          return { data: { ok: true } }; // no extra select; UI has the data already
        },
        invalidatesTags: ["reports"],
      },
    ),

    updateDeputyOtherReportStatus: build.mutation<
      any,
      UpdateDeputyReportParams
    >({
      queryFn: async ({ id, status, admin_comment, admin_user_id }) => {
        const { error } = await supabase
          .from("dupty_report") // ensure correct table name
          .update({
            status,
            admin_comment,
            status_updated_by: admin_user_id,
          })
          .eq("id", id);

        if (error) return { error };
        return { data: { ok: true } }; // minimal success payload
      },
      invalidatesTags: ["reports"],
    }),

    updatePublicReportStatus: build.mutation<any, any>({
      queryFn: async (params: any) => {
        console.log(params);
        let { data } = await supabase
          .from("report")
          .update({
            status: params.status,
            status_updated_by: params.user_id,
          })
          .eq("id", params.id)
          .single();
        return { data };
      },
      invalidatesTags: ["reports"],
    }),

    getClaimedTicketRequestsFromDeputies: build.query<any[], any>({
      queryFn: async (params) => {
        const { data, error } = await supabase.rpc(
          "get_claimed_ticket_requests_from_deputies",
          params,
        );

        if (error) throw error;

        return { data: data ?? [] };
      },
    }),

    // getClaimedTicketRequestsFromDeputiesCount: build.query<number, any>({
    //   queryFn: async (params) => {
    //     const { data, error } = await supabase.rpc(
    //       "get_claimed_ticket_requests_from_deputies_count",
    //       params,
    //     );

    //     if (error) throw error;

    //     return { data: Number(data ?? 0) };
    //   },
    // }),

    deleteClaimedTicketRequest: build.mutation<
      {
        success: boolean;
        message: string;
        deleted_id: number | null;
      },
      {
        p_ticket_request_id: number;
      }
    >({
      queryFn: async (params) => {
        const { data, error } = await supabase.rpc(
          "delete_claimed_ticket_request_if_no_review",
          params,
        );

        if (error) throw error;

        const result = Array.isArray(data) ? data[0] : data;

        return {
          data: {
            success: Boolean(result?.success),
            message: result?.message ?? "",
            deleted_id: result?.deleted_id ?? null,
          },
        };
      },
    }),
    getNoReportDeputies: build.query<
      { data: any[]; count: number },
      {
        page?: number;
        rowsPerPage?: number;
        deputyName?: string;
        deputyControlNumber?: string;
        deputyEndorser?: string;
        sortColumn?: string;
        sortDirection?: string;
      }
    >({
      queryFn: async ({
        page = 1,
        rowsPerPage = 100,
        deputyName = "",
        deputyControlNumber = "",
        deputyEndorser = "",
        sortColumn = "card_issued_date",
        sortDirection = "asc",
      }) => {
        const { data, error } = await supabase.rpc(
          "get_deputies_no_reports_last_3_months",
          {
            p_page: page,
            p_rows_per_page: rowsPerPage,
            p_deputy_name: deputyName,
            p_deputy_control_number: deputyControlNumber,
            p_deputy_endorser: deputyEndorser,
            p_sort_column: sortColumn,
            p_sort_direction: sortDirection,
          },
        );

        if (error) return { error };

        return {
          data: {
            data: data || [],
            count: data?.[0]?.total_count || 0,
          },
        };
      },
      providesTags: ["reports"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDeputyReportIdQuery,
  useGetTVChannelReportsQuery,
  useGetDeputyReportsQuery,
  useGetDeputyReportAttachmentsQuery,
  useLazyGetDeputyReportIdQuery,
  useDeleteTVChannelReportMutation,
  useDeletePublicReportMutation,
  useLazyGetTVChannelReportsQuery,
  useLazyGetDeputyReportsQuery,
  useGetPublicReportsQuery,
  useGetPublicReportByIdQuery,
  useGetDateFilteredPublicReportsQuery,
  useAddPublicResponseMutation,
  useAddTVChannelReportResponseMutation,
  useAddDeputyResponseMutation,
  useDeleteDeputyReportMutation,
  useGetTVProgramReportsQuery,
  useGetDeputyTVReportsQuery,
  useLazyGetDeputyTVReportsQuery,
  useUpdateDeputyTVReportStatusMutation,
  useUpdateDeputyOtherReportStatusMutation,
  useUpdatePublicReportStatusMutation,
  useGetDeputyTVReportByIdQuery,
  useGetDeputyReportByIdQuery,
  useGetDeputyReportStatisticsQuery,
  useGetDeputyReportSummaryQuery,
  useGetDeputyReportSummaryTwoQuery,
  useLazyGetDeputyReportSummaryTwoQuery,
  useGetClaimedTicketRequestsFromDeputiesQuery,
  useLazyGetClaimedTicketRequestsFromDeputiesQuery,
  // useGetClaimedTicketRequestsFromDeputiesCountQuery,
  useDeleteClaimedTicketRequestMutation,
  useGetNoReportDeputiesQuery,
  useLazyGetNoReportDeputiesQuery,
  useGetDeputyDashboardSummaryV3Query,
  useGetDeputyDashboardMonthlyTrendQuery,
  useGetDistinctEndorsersQuery,
} = reportApi;
